import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreObject, useApolloClient } from '@apollo/client';
import { Avatar, Modal, Spin, Upload, notification } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { CameraIcon, TrashIcon } from 'components/icons';
import {
  UploadType,
  useEmployee,
  useImageBlobSrc,
  useToken,
  useUploadDeleteUri,
} from 'hooks';
import styles from './styles.less';

type ProfilePictureProps = {
  photo: string;
  placeholder?: React.ReactNode;
  allowEdit?: boolean;
};

export const ProfilePicture: FC<ProfilePictureProps> = ({
  photo,
  placeholder,
  allowEdit = false,
}) => {
  const supportedImageTypes = ['image/png', 'image/jpeg'];
  const { uploadURI, deleteResource } = useUploadDeleteUri(UploadType.AVATAR);
  const client = useApolloClient();
  const { employee } = useEmployee();
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useTranslation('translation');
  const token = useToken();
  const { isImageLoading, src, clearImageResources } = useImageBlobSrc(photo);

  const onUploadingStateChange = ({
    file,
  }: UploadChangeParam<UploadFile<string>>): void => {
    if (file.status === 'error') {
      setIsUploading(false);
      void notification.error({
        message: t('Error occurred while uploading the image.'),
      });
    }

    if (file.status === 'uploading') {
      setIsUploading(true);
    }

    if (file.status === 'done') {
      notification.success({
        message: 'Avatar has been saved!',
      });
      client.cache.modify({
        fields: {
          photo() {
            return file.response;
          },
        },
        id: client.cache.identify(employee as unknown as StoreObject),
      });
      clearImageResources();
      setIsUploading(false);
    }
  };

  const beforeUpload = (file: RcFile | File): boolean => {
    if (!supportedImageTypes.includes(file.type)) {
      void notification.error({
        message: 'Incorrect extension. Available: PNG, JPEG',
      });

      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 8;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      void notification.error({
        message: 'Incorrect file size. 5Mb available',
      });

      return false;
    }

    return true;
  };

  const onAvatarDelete = (): void =>
    void Modal.confirm({
      centered: true,
      content: t('Are you sure you want to delete your avatar?'),
      okText: t('Yes, delete'),
      onOk: () => {
        void deleteResource().then(() => {
          notification.success({
            message: t('Avatar has been deleted!'),
          });

          client.cache.modify({
            fields: {
              photo() {
                return '';
              },
            },
            id: client.cache.identify(employee as unknown as StoreObject),
          });
        });
      },
      title: t('Confirm Action?'),
    });

  return (
    <div className={styles['profile-picture']}>
      <>
        {isUploading || isImageLoading ? (
          <Spin className={styles['spinner']} />
        ) : (
          <Avatar
            className={styles['profile-img']}
            src={src}
            alt="Profile"
            size={220}
          >
            {placeholder}
          </Avatar>
        )}
      </>
      {allowEdit && (
        <div className={styles['profile-picture-tools-container']}>
          <ImgCrop beforeCrop={beforeUpload}>
            <Upload
              headers={{ Authorization: `Bearer ${token}` }}
              data={{ uploadURI }}
              action={uploadURI}
              accept={supportedImageTypes.join(',')}
              multiple={false}
              showUploadList={false}
              maxCount={1}
              beforeUpload={beforeUpload}
              onChange={onUploadingStateChange}
              className={styles['d-flex']}
            >
              <CameraIcon width={20} />
            </Upload>
          </ImgCrop>
          {photo !== '' && <TrashIcon width={20} onClick={onAvatarDelete} />}
        </div>
      )}
    </div>
  );
};
